<template>
  <div class="w-100">
    <div>
      <div class="form-input clickable" :class="[ size, { disabled: disabled, active: value, 'border-danger': errors && errors.length > 0 } ]" @click="active = !active">
        <label v-if="title && title.length" :for="randomId" class="form-input-label">
          <span>{{ title }}</span> <span v-if="required" class="ms-2"><i class="fas fa-asterisk text-danger" style="font-size: 0.7em"></i></span>
        </label>
        <div class="form-input-body" :id="randomId">
          <div class="me-2">
            <div v-if="multiple" class="d-flex align-items-center g-1 flex-wrap">
              <span v-for="option in selectedOptions" :key="option.id">
                <slot name="option" :option="option" :preview="true"></slot>
              </span>
              <span v-if="selectedOptions.length === 0">&nbsp;</span>
            </div>
            <div v-else>
              <slot v-if="selectedOption" name="option" :option="selectedOption" :preview="true"></slot>
              <span v-else>&nbsp;</span>
            </div>
          </div>
        </div>
        <div class="position-absolute d-flex align-items-center" style="top: 0; bottom: 0; right: 0; margin-right: 0.8em">
          <i class="fas" :class="{ 'fa-angle-down': !active, 'fa-angle-up': active }"></i>
        </div>
      </div>
      <div v-if="errors && errors.length > 0" class="form-input-errors">
        <div v-for="(error, index) in errors" :key="index" class="text-danger">{{ error }}</div>
      </div>
    </div>
    <ModalView v-model:active="active" :size="size">
      <template v-slot:header>
        <div class="d-flex align-items-center justify-content-between">
          <h3 class="mb-0">{{ title }}</h3>
          <ModalCloseButtonView @click="active = false"/>
        </div>
        <div v-if="searchable" class="w-100 mt-3">
          <InputView type="text" v-model:value="searchQuery"/>
        </div>
      </template>
      <template v-slot:body>
        <div v-if="options && options.length > 0" class="list-group">
          <button v-for="option in options" class="list-group-item list-group-item-action" :class="{ 'opacity-75': max != null && selectedOptions.length >= max && !isOptionSelected(option) }" :key="option.id" @click="optionSelected(option)" :disabled="max != null && selectedOptions.length >= max && !isOptionSelected(option)" style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
            <span class="d-flex align-items-center">
              <CheckboxView :round="!multiple" :active="isOptionSelected(option)" color="primary" style="margin-right: 0.6em"/>
              <slot name="option" :option="option" :preview="false"></slot>
            </span>
          </button>
        </div>
      </template>
    </ModalView>
  </div>
</template>

<script setup>
import {computed, ref, watch} from "vue";
import InputView from "./InputView.vue";
import ModalView from "../../../plugins/modal/ModalView.vue";
import ModalCloseButtonView from "../../../plugins/modal/ModalCloseButtonView.vue";
import CheckboxView from "./CheckboxView.vue";

const props = defineProps({
  title: String,
  options: Array,
  value: [Number, String, Boolean, Array],
  required: Boolean,
  multiple: Boolean,
  disabled: Boolean,
  searchable: Boolean,
  max: Number,
  size: {
    type: String,
    validator(value) {
      return ["small", "medium", "large"].includes(value);
    }
  },
  errors: Array,
  query: String
});

const emit = defineEmits(["update:value", "update:query"]);

const searchQuery = ref("");
const active = ref(false);
const randomId = "input-" + Math.random().toString();

function optionSelected(option) {
  if (props.multiple) {
    let list = [...selectedOptions.value];
    if (isOptionSelected(option)) {
      list = list.filter(item => item.id !== option.id);
    } else {
      list.push(option);
    }
    emit("update:value", list.map(item => item.id));
  } else {
    if (isOptionSelected(option)) {
      if (!props.required) {
        emit("update:value", undefined);
      }
    } else {
      emit("update:value", option.id);
    }
    searchQuery.value = "";
    active.value = false;
  }
}

function isOptionSelected(option) {
  if (!selectedOption) return false;
  if (props.multiple) {
    return selectedOptions.value.some(item => item.id === option.id);
  } else {
    return selectedOption.value?.id === option.id;
  }
}

watch(active, value => {
  if (!value) {
    searchQuery.value = "";
  }
});

watch(searchQuery, value => {
  emit("update:query", value);
});

const selectedOption = computed(() => {
  if (props.value == null) return undefined;
  if (props.options.length > 0) {
    return props.options.find((item) => item.id === props.value);
  }
  return undefined;
});

const selectedOptions = computed(() => {
  if (props.value == null) return [];
  if (props.options.length > 0) {
    return props.options.filter((item) => props.value.includes(item.id));
  }
  return [];
});

</script>